import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { VmeCommonModule } from '../../../vme-common.module';
import { VmePipesModule } from '../../../vme-pipes.module';
import {
  AddressAutoCompletionService,
  AutoCompleteAddressDetailsFormatted,
} from '../../../services/address-auto-completion.service';
import { MessageService } from 'primeng';
import { HttpErrorResponse } from '@angular/common/http';
import { Message } from '../../../classes/Message';
import {
  CarryoverService,
  SettingsService,
  SupplierInboxJobsService,
  UserService,
} from '../../../services';
import { ICarryoverAddress } from '../../../interfaces/travel-scheduler/travel-scheduler-task';
import { IVehicleAddress } from '../../../interfaces/task-info';
import { ValidateCarryoverAddressResponse } from '../../../interfaces/carryover/validate-carryover-address';
import { CarryoverAddressValidationOutcome } from '../../../enums/carryover-address-validation-outcome.enum';

export type ParkingOption = 'home' | 'other';

@Component({
  selector: 'carryover-address-selector',
  standalone: true,
  imports: [CommonModule, VmeCommonModule, VmePipesModule],
  templateUrl: './carryover-address-selector.component.html',
  styleUrls: ['./carryover-address-selector.component.scss'],
})
export class CarryoverAddressSelectorComponent implements OnInit, OnChanges {
  @Input() supplierId: number;
  @Input() taskId: number;
  @Input() jobId: number;
  @Input() isVME: boolean = false;

  /** When this is in the past the supplier has to ask operations to hand the job back for them. */
  @Input() latestHandBackDateTime: Date | null = null;

  protected readonly ValidationOutcome = CarryoverAddressValidationOutcome;

  parkingOption: ParkingOption = null;
  parkingLocation: string;

  showOtherInput: boolean;

  isValidating: boolean = false;
  validationFailed: boolean = false;

  private lastValidatedAddress: ICarryoverAddress = null;
  private validationRequestId: number = 0;

  @Input() selectedLocation: ICarryoverAddress;
  @Input() validationResult: ValidateCarryoverAddressResponse = null;

  @Output() selectedLocationChange: EventEmitter<ICarryoverAddress> = new EventEmitter();

  @Output() validationResultChange: EventEmitter<ValidateCarryoverAddressResponse> =
    new EventEmitter();

  /** Raised once the job has been handed back, so the schedule can be closed. */
  @Output() handedBack: EventEmitter<void> = new EventEmitter();

  showHandbackDialog: boolean = false;
  isHandingBack: boolean = false;
  engineiusPhone: string;

  constructor(
    private addressAutoCompletionService: AddressAutoCompletionService,
    private userService: UserService,
    private carryoverService: CarryoverService,
    private supplierInboxJobsService: SupplierInboxJobsService,
    private settingsService: SettingsService,
    private messageService: MessageService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedLocation) {
      this.syncParkingOption();
    }
  }

  private syncParkingOption() {
    if (!this.selectedLocation) {
      return;
    }

    if (this.selectedLocation.isSupplierHome) {
      this.parkingOption = 'home';
      this.showOtherInput = false;
    } else {
      this.parkingOption = 'other';
      this.showOtherInput = false;
    }
  }

  ngOnInit(): void {
    this.syncParkingOption();

    const addressSearchFields = [
      {
        element: 'otherParkingLocation',
        field: '',
        parentContainer: 'otherParkingLocation',
        showSavedSites: false,
      },
    ];

    this.addressAutoCompletionService.useWithVehicleAddressConverter(
      addressSearchFields,
      (address) => {
        console.log(address);
        const mappedAddress = this.mapToCarryoverAddress(address);
        // this.parkingLocation = mappedAddress.addressLine;
        this.selectedLocationChange.emit(mappedAddress);
        this.showOtherInput = false;

        // The address is only validated once one has been picked from the search results, never while
        // the supplier is still typing.
        this.validateAddress(mappedAddress);
      },
      () => {},
      () => {},
      10000,
    );
  }

  mapToCarryoverAddress(address: IVehicleAddress): ICarryoverAddress {
    let primaryAddress = address.primaryAddress;

    if ((address.businessName || '').length > 0 && !primaryAddress.includes(address.businessName)) {
      primaryAddress = `${address.businessName}, ${primaryAddress}`;
    }

    return {
      isSupplierHome: false,
      addressLine: primaryAddress,
      businessName: address.businessName,
      latitude: address.latitude,
      longitude: address.longitude,
      postcode: address.postcode,
    };
  }

  onHomeChosen() {
    let carryoverLocation: ICarryoverAddress = {
      isSupplierHome: true,
    };

    this.selectedLocationChange.emit(carryoverLocation);
    this.showOtherInput = false;

    this.validateAddress(carryoverLocation);
  }

  onOtherChosen() {
    this.parkingLocation = null;
    this.showOtherInput = true;
    this.resetValidation();
    this.selectedLocationChange.emit(null);
  }

  onEditOtherLocation() {
    this.resetValidation();
    this.selectedLocationChange.emit(null);
    this.showOtherInput = true;
  }

  /**
   * The supplier can only hand a job back themselves during the cooling off period, which ends at
   * latestHandBackDateTime. After that they have to ask operations to do it for them.
   */
  get canHandBackImmediately(): boolean {
    if (this.latestHandBackDateTime == null) {
      return false;
    }

    return new Date() < new Date(this.latestHandBackDateTime);
  }

  get handbackButtonLabel(): string {
    return this.canHandBackImmediately ? 'Handback' : 'Request to Handback';
  }

  async onRequestHandback() {
    if (!this.canHandBackImmediately && !this.engineiusPhone) {
      this.engineiusPhone = (await this.settingsService.GetSystemSettings())?.engineiusPhone;
    }

    this.showHandbackDialog = true;
  }

  async onConfirmHandback() {
    this.isHandingBack = true;

    try {
      await this.supplierInboxJobsService.supplierHandBack(this.jobId);

      this.showHandbackDialog = false;
      this.messageService.add(Message.Success('', 'Hand back successful'));
      this.handedBack.emit();
    } catch (error) {
      const detail =
        error instanceof HttpErrorResponse && error.status == 422
          ? error.error
          : 'An unknown error occurred, please contact Engineius';

      this.messageService.add(Message.Error(detail, 'Hand Back Error'));
    } finally {
      this.isHandingBack = false;
    }
  }

  private setValidationResult(result: ValidateCarryoverAddressResponse) {
    this.validationResult = result;
    this.validationResultChange.emit(result);
  }

  /**
   * Checks the given address with the backend. The address is passed in rather than read from
   * selectedLocation, because the parent owns that and has not been given the new address yet.
   */
  private async validateAddress(address: ICarryoverAddress) {
    if (this.taskId == null || !address) {
      return;
    }

    this.lastValidatedAddress = address;

    const requestId = ++this.validationRequestId;

    this.isValidating = true;
    this.validationFailed = false;
    this.setValidationResult(null);

    try {
      const result = await this.carryoverService.validateCarryoverAddress({
        taskId: this.taskId,
        supplierId: this.supplierId,
        isSupplierHomeAddress: address.isSupplierHome,
        addressLine: address.isSupplierHome ? null : address.addressLine,
        postcode: address.isSupplierHome ? null : address.postcode,
      });

      // Ignore a response the supplier has already moved on from.
      if (requestId != this.validationRequestId) {
        return;
      }

      this.setValidationResult(result);
    } catch (error) {
      if (requestId != this.validationRequestId) {
        return;
      }

      console.error('Error validating carryover address', error);
      this.validationFailed = true;
    } finally {
      if (requestId == this.validationRequestId) {
        this.isValidating = false;
      }
    }
  }

  onRetryValidation() {
    this.validateAddress(this.lastValidatedAddress);
  }

  /** Clears the validation state and abandons any validation still in flight. */
  private resetValidation() {
    this.validationRequestId++;
    this.isValidating = false;
    this.validationFailed = false;
    this.lastValidatedAddress = null;
    this.setValidationResult(null);
  }
}
